import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ChevronRight, Database, MonitorSmartphone, RefreshCw, ScrollText, Server, User,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

function SettingsLink({ to, icon: Icon, title, description, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex min-h-11 items-center gap-3 rounded-xl px-3 py-3 hover:bg-white/[0.05] transition-colors"
    >
      <div className="size-10 rounded-xl bg-white/[0.05] text-zinc-300 grid place-items-center shrink-0">
        <Icon className="size-[18px]" strokeWidth={1.7} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-zinc-200">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{description}</div>
      </div>
      <ChevronRight className="size-4 shrink-0 text-zinc-600 group-hover:text-zinc-300 transition-colors" strokeWidth={1.7} />
    </Link>
  );
}

function StatusPill({ ok, children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${ok ? "text-emerald-400" : "text-red-400"}`}>
      <span className={`size-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
      {children}
    </span>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticError, setDiagnosticError] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);

  const loadDiagnostic = useCallback(async () => {
    if (!isAdmin) return;
    setDiagnosticLoading(true);
    setDiagnosticError(null);
    try {
      const response = await api.get("/system/diagnostic");
      setDiagnostic(response.data);
    } catch (error) {
      setDiagnosticError(formatApiError(error));
    } finally {
      setDiagnosticLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { loadDiagnostic(); }, [loadDiagnostic]);

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Paramètres"
        description="Votre compte et les outils d’administration de CAM."
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-10 max-w-4xl space-y-4">
        <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] overflow-hidden">
          <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 flex items-center gap-2">
            <User className="size-4 text-zinc-400" strokeWidth={1.7} />
            <h2 className="text-sm font-semibold text-zinc-200">Compte</h2>
          </div>
          <div className="px-4 sm:px-5 pb-5 space-y-3">
            <Row label="Nom" value={user?.name} />
            <Row label="Adresse e-mail" value={user?.email} />
            <Row label="Rôle" value={isAdmin ? "Administrateur" : "Utilisateur"} />
          </div>
        </section>

        {isAdmin && (
          <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] overflow-hidden" data-testid="settings-diagnostic">
            <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <Activity className="size-4 shrink-0 text-zinc-400" strokeWidth={1.7} />
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-zinc-200">Diagnostic</h2>
                  <p className="text-xs text-zinc-500 mt-0.5">État rapide de CAM et de ses caméras.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={loadDiagnostic}
                disabled={diagnosticLoading}
                className="size-11 shrink-0 grid place-items-center rounded-xl border border-white/[0.08] text-zinc-400 hover:text-white hover:bg-white/[0.05] disabled:opacity-40 transition-colors"
                data-testid="settings-diagnostic-refresh"
                aria-label="Actualiser le diagnostic"
                title="Actualiser"
              >
                <RefreshCw className={`size-4 ${diagnosticLoading ? "animate-spin" : ""}`} strokeWidth={1.7} />
              </button>
            </div>

            <div className="px-4 sm:px-5 pb-5">
              {diagnosticError ? (
                <div className="rounded-xl border border-red-500/20 bg-red-500/[0.06] px-3 py-2 text-xs text-red-300" data-testid="settings-diagnostic-error">
                  {diagnosticError}
                </div>
              ) : diagnostic ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Metric label="Caméras" value={diagnostic.cameras?.total ?? 0} />
                    <Metric label="En ligne" value={diagnostic.cameras?.online ?? 0} tone="online" />
                    <Metric label="Hors ligne" value={diagnostic.cameras?.offline ?? 0} />
                    <Metric label="Désactivées" value={diagnostic.cameras?.disabled ?? 0} />
                  </div>

                  <div className="border-t border-white/[0.06] pt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    <InfoRow
                      icon={Server}
                      label="API"
                      value={<StatusPill ok={diagnostic.api_status === "ok"}>{diagnostic.api_status === "ok" ? "Opérationnelle" : "Erreur"}</StatusPill>}
                    />
                    <InfoRow
                      icon={Database}
                      label="Base de données"
                      value={<StatusPill ok={diagnostic.database_status === "ok"}>{diagnostic.database_status === "ok" ? "Connectée" : "Erreur"}</StatusPill>}
                    />
                    <InfoRow icon={Activity} label="Version CAM" value={`v${diagnostic.version || "—"}`} />
                    <InfoRow icon={Activity} label="Protocole agent" value={diagnostic.agent_protocol_version || "—"} />
                  </div>
                </div>
              ) : (
                <div className="py-5 text-sm text-zinc-500 animate-pulse" data-testid="settings-diagnostic-loading">Chargement du diagnostic…</div>
              )}
            </div>
          </section>
        )}

        {isAdmin && (
          <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] overflow-hidden">
            <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-1">
              <h2 className="text-sm font-semibold text-zinc-200">Journal</h2>
            </div>
            <div className="px-2 pb-2">
              <SettingsLink
                to="/audit"
                icon={ScrollText}
                title="Journal d’activité"
                description="Consulter les authentifications, ajouts, modifications et opérations sensibles."
                testid="settings-audit-link"
              />
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] overflow-hidden">
          <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-1">
            <h2 className="text-sm font-semibold text-zinc-200">Avancé</h2>
          </div>
          <div className="px-2 pb-2">
            <SettingsLink
              to="/device-camera"
              icon={MonitorSmartphone}
              title="Utiliser cet appareil comme caméra"
              description="Diffuser la caméra de ce téléphone ou de ce PC vers CAM."
              testid="settings-legacy-device-link"
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-black/20 px-3 py-3">
      <div className={`text-xl font-semibold ${tone === "online" ? "text-emerald-400" : "text-zinc-100"}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col items-start gap-1 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <span className="flex items-center gap-2 text-zinc-500">
        <Icon className="size-3.5" strokeWidth={1.6} />
        {label}
      </span>
      <span className="text-left text-zinc-200 sm:text-right">{value}</span>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col items-start gap-1 text-sm sm:flex-row sm:justify-between sm:gap-4">
      <span className="text-zinc-500">{label}</span>
      <span className="break-all text-left text-zinc-200 sm:text-right">{value || "—"}</span>
    </div>
  );
}
